/* eslint-disable react-hooks/exhaustive-deps */
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SecurityIcon from "@mui/icons-material/Security";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createRole,
  getPermissions,
  getRoleData,
} from "../../../features/users/roleSlice";
import HomeLayout from "../../../layouts/Home";
import { vCreateRole } from "../../../utils/validators";

const CreateRole = ({ stateModel, editable }) => {
  const navigate = useNavigate();
  const { permissions, model, loading } = useSelector(getRoleData);
  const dispatch = useDispatch();
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    handleFocus,
    setFieldValue,
  } = useFormik({
    validationSchema: vCreateRole,
    initialValues: stateModel || model,
    onSubmit: async () => {
      const permissions = selectedPermissions;

      const payload = { ...values, permissions };

      const action = editable ? createRole : createRole;
      const res = await dispatch(action(payload)).unwrap();

      res?.success === true && navigate(-1);
    },
  });

  useEffect(() => {
    dispatch(getPermissions());
  }, []);

  // Initialize selectedPermissions from stateModel when editing
  useEffect(() => {
    console.log(stateModel);
    if (
      stateModel?.permissions &&
      Array.isArray(stateModel.permissions) &&
      permissions?.length > 0
    ) {
      setSelectedPermissions(stateModel.permissions);
      setFieldValue("permissions", stateModel.permissions);
    }
  }, [stateModel, permissions]);

  // Group permissions by module (based on entity name)
  const groupedPermissions = useMemo(() => {
    const groups = {};

    // Define module mappings
    const moduleMapping = {
      USER: "User Management",
      ROLE: "Role Management",
      PERMISSION: "Permission Management",
      COMPANY: "Company Management",
      HOSPITAL: "Hospital Management",
      HMO: "HMO Management",
      EMPLOYEE: "Employee Management",
      PATIENT: "Patient Management",
      CONSULTATION: "Consultation Management",
      PLAN: "Plan Management",
      SERVICE: "Service Management",
      AILMENT: "Ailment Management",
      GRADE: "Grade Management",
      TRANSACTION: "Transaction Management",
      PAYMENT: "Payment Management",
      APPOINTMENT: "Appointment Management",
      STAFF: "Staff Management",
      DESIGNATION: "Designation Management",
      ACCOUNT: "Account Management",
      DASHBOARD: "Dashboard",
      REPORT: "Reports",
      SETTING: "Settings",
      CONFIGURATION: "Configuration",
    };

    permissions?.forEach((permission) => {
      let moduleName = "General";

      // Extract the entity name from permission caption
      // Format examples: "CREATE_USER", "UPDATE_HOSPITAL", "READ_PLAN"
      const parts = permission.caption.split("_");

      if (parts.length >= 2) {
        // Get the entity part (everything after the action)
        const entity = parts.slice(1).join("_");
        moduleName =
          moduleMapping[entity] ||
          `${
            entity.charAt(0) + entity.slice(1).toLowerCase().replace(/_/g, " ")
          } Management`;
      }

      if (!groups[moduleName]) {
        groups[moduleName] = [];
      }
      groups[moduleName].push(permission);
    });

    // Sort groups alphabetically
    const sortedGroups = {};
    Object.keys(groups)
      .sort()
      .forEach((key) => {
        sortedGroups[key] = groups[key];
      });

    return sortedGroups;
  }, [permissions]);

  const handlePermissionToggle = (permissionCaption) => {
    const newPermissions = selectedPermissions.includes(permissionCaption)
      ? selectedPermissions.filter((p) => p !== permissionCaption)
      : [...selectedPermissions, permissionCaption];

    setSelectedPermissions(newPermissions);
    setFieldValue("permissions", newPermissions);
  };

  const handleSelectAllInCategory = (category) => {
    const categoryPermissions = groupedPermissions[category].map(
      (p) => p.caption
    );
    const allSelected = categoryPermissions.every((p) =>
      selectedPermissions.includes(p)
    );

    let newPermissions;
    if (allSelected) {
      // Deselect all in this category
      newPermissions = selectedPermissions.filter(
        (p) => !categoryPermissions.includes(p)
      );
    } else {
      // Select all in this category
      const toAdd = categoryPermissions.filter(
        (p) => !selectedPermissions.includes(p)
      );
      newPermissions = [...selectedPermissions, ...toAdd];
    }

    setSelectedPermissions(newPermissions);
    setFieldValue("permissions", newPermissions);
  };

  const isCategoryFullySelected = (category) => {
    const categoryPermissions = groupedPermissions[category].map(
      (p) => p.caption
    );
    return categoryPermissions.every((p) => selectedPermissions.includes(p));
  };

  const isCategoryPartiallySelected = (category) => {
    const categoryPermissions = groupedPermissions[category].map(
      (p) => p.caption
    );
    return (
      categoryPermissions.some((p) => selectedPermissions.includes(p)) &&
      !isCategoryFullySelected(category)
    );
  };

  return (
    <HomeLayout
      title="Role Management"
      subtitle="Configure roles and manage access permissions"
    >
      {/* Header */}
      <Box className="flex items-center gap-2 mb-6">
        <ArrowBackIcon
          onClick={() => navigate(-1)}
          className="cursor-pointer text-[var(--c-dark-1)] hover:text-[var(--c-primary-0)] transition-colors"
          fontSize="medium"
        />
        <Typography className="text-[16px] font-medium text-[var(--c-dark-1)]">
          Go back
        </Typography>
      </Box>

      {/* Page Title */}
      <Box className="mb-6">
        <Box className="flex items-center gap-2 mb-2">
          {/* <SecurityIcon className="text-[var(--c-primary-0)]" /> */}
          <Typography variant="h5" className="font-semibold text-[#040316]">
            {editable ? "Edit" : "Create New"} Role
          </Typography>
        </Box>
        <Typography variant="body2" className="text-gray-600">
          {editable
            ? "Update role information and permissions"
            : "Define a new role with specific permissions"}
        </Typography>
      </Box>

      {/* Main Form */}
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          {/* Role Information Card */}
          <Grid item xs={12} lg={4}>
            <Box className="sticky top-6">
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <Typography
                    variant="h6"
                    className="font-bold text-[#040316] mb-4"
                  >
                    Role Information
                  </Typography>
                  <Divider className="mb-6" />

                  <FormControl fullWidth>
                    <TextField
                      className="bg-white"
                      id="name"
                      label="Role Name"
                      name="name"
                      placeholder="e.g., Admin, Manager, Viewer"
                      value={values.name}
                      required
                      onFocus={handleFocus}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.name && touched.name}
                      helperText={
                        errors.name && touched.name ? errors.name : ""
                      }
                    />
                  </FormControl>

                  {/* Summary */}
                  <Box className="mt-6 p-4 bg-purple-50 rounded-lg">
                    <Typography className="text-sm font-semibold text-[#8b5cf6] mb-2">
                      Permission Summary
                    </Typography>
                    <Box className="flex items-center justify-between">
                      <Typography className="text-sm text-gray-600">
                        Selected Permissions:
                      </Typography>
                      <Chip
                        label={selectedPermissions.length}
                        size="small"
                        sx={{
                          backgroundColor: "#8b5cf6",
                          color: "white",
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Box className="flex items-center justify-between mt-2">
                      <Typography className="text-sm text-gray-600">
                        Total Available:
                      </Typography>
                      <Chip
                        label={permissions?.length || 0}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: "#8b5cf6",
                          color: "#8b5cf6",
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>

          {/* Permissions Card */}
          <Grid item xs={12} lg={8}>
            <Card className="shadow-sm">
              <CardContent className="p-6">
                <Box className="flex items-center justify-between mb-4">
                  <Typography variant="h6" className="font-bold text-[#040316]">
                    Permissions
                  </Typography>
                  <Typography className="text-sm text-gray-500">
                    Select permissions to grant to this role
                  </Typography>
                </Box>
                <Divider className="mb-4" />

                {Object.keys(groupedPermissions).length === 0 ? (
                  <Box className="text-center py-8">
                    <Typography className="text-gray-500">
                      Loading permissions...
                    </Typography>
                  </Box>
                ) : (
                  <Box className="space-y-4">
                    {Object.entries(groupedPermissions).map(
                      ([category, perms]) => (
                        <Card
                          key={category}
                          className="border border-gray-200 shadow-none"
                        >
                          <CardContent className="p-4">
                            {/* Category Header */}
                            <Box className="flex items-center justify-between mb-3">
                              <Box className="flex items-center gap-2">
                                <Checkbox
                                  checked={isCategoryFullySelected(category)}
                                  indeterminate={isCategoryPartiallySelected(
                                    category
                                  )}
                                  onChange={() =>
                                    handleSelectAllInCategory(category)
                                  }
                                  sx={{
                                    color: "#8b5cf6",
                                    "&.Mui-checked": {
                                      color: "#8b5cf6",
                                    },
                                    "&.MuiCheckbox-indeterminate": {
                                      color: "#8b5cf6",
                                    },
                                  }}
                                />
                                <Typography className="font-semibold text-[#040316] text-lg">
                                  {category}
                                </Typography>
                                <Chip
                                  label={`${
                                    perms.filter((p) =>
                                      selectedPermissions.includes(p.caption)
                                    ).length
                                  }/${perms.length}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#f3f4f6",
                                    fontSize: "0.75rem",
                                  }}
                                />
                              </Box>
                            </Box>

                            {/* Permissions in Category */}
                            <Grid container spacing={1} className="ml-8">
                              {perms.map((permission) => (
                                <Grid item xs={12} sm={6} key={permission.id}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedPermissions.includes(
                                          permission.caption
                                        )}
                                        onChange={() =>
                                          handlePermissionToggle(
                                            permission.caption
                                          )
                                        }
                                        sx={{
                                          color: "#8b5cf6",
                                          "&.Mui-checked": {
                                            color: "#8b5cf6",
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Box className="flex items-center gap-1">
                                        <Typography className="text-sm text-gray-700">
                                          {permission.caption
                                            .split("_")
                                            .map(
                                              (word) =>
                                                word.charAt(0) +
                                                word.slice(1).toLowerCase()
                                            )
                                            .join(" ")}
                                        </Typography>
                                        {selectedPermissions.includes(
                                          permission.caption
                                        ) && (
                                          <CheckCircleIcon
                                            sx={{
                                              fontSize: 16,
                                              color: "#8b5cf6",
                                            }}
                                          />
                                        )}
                                      </Box>
                                    }
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      )
                    )}
                  </Box>
                )}

                {errors.permissions && touched.permissions && (
                  <FormHelperText error className="mt-2">
                    {errors.permissions}
                  </FormHelperText>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Submit Button */}
        <Box className="flex justify-end mt-6">
          <LoadingButton
            className="bg-[var(--c-primary-0)] hover:bg-[var(--c-primary-1)] shadow-md normal-case px-8"
            size="large"
            type="submit"
            variant="contained"
            loading={loading}
            startIcon={<SecurityIcon />}
          >
            {editable ? "Update" : "Create"} Role
          </LoadingButton>
        </Box>
      </Box>
    </HomeLayout>
  );
};

export default CreateRole;
